'use client';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff, Mail, Wand2 } from 'lucide-react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { Logo } from '@/components/ui/logo';

import { authClient } from '@/lib/auth/client';

// ─── Shared tab trigger style ─────────────────────────────────────────────────

const triggerClass =
  'flex-1 rounded-none bg-transparent text-muted-foreground ' +
  'data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none ' +
  'border-b-2 border-transparent data-[state=active]:border-primary ' +
  'px-2 py-2.5 text-[13px] font-medium gap-1.5 transition-all duration-200 hover:text-foreground';

// ─── Email + Password ─────────────────────────────────────────────────────────

const emailPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'This field is required')
    .email('Please enter a valid email address'),
  password: z.string().min(1, 'This field is required'),
});
type EmailPasswordValues = z.infer<typeof emailPasswordSchema>;

function EmailPasswordTab() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<EmailPasswordValues>({
    resolver: zodResolver(emailPasswordSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: EmailPasswordValues) {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.signIn.email({
        email: values.email,
        password: values.password,
      });

      if (result.error) {
        const errAny = result.error as Record<string, unknown>;
        if (errAny.code === 'USER_BANNED') {
          setError(
            'Your account has been suspended. Contact support for assistance.'
          );
        } else {
          setError('Invalid email or password. Please try again.');
        }
        return;
      }

      if (!result.data) {
        setError('Invalid email or password. Please try again.');
        return;
      }

      const dataAny = result.data as Record<string, unknown>;

      // Check if 2FA redirect is needed
      if (dataAny.twoFactorRedirect === true) {
        router.push('/auth/mfa');
        return;
      }

      // Route based on role
      const user = dataAny.user as Record<string, unknown> | undefined;
      const role = user?.role as string | undefined;

      if (role === 'admin') {
        router.push('/admin/dashboard');
      } else if (role === 'investor') {
        router.push('/investor/dashboard');
      } else if (role === 'founder') {
        router.push('/founder/dashboard');
      } else {
        // No role assigned — send to no-access page
        router.push('/auth/no-access');
      }
      // Note: MFA enforcement for admin/investor roles is handled by their
      // respective dashboard layouts (Track 3 will add MFA gate)
    } catch {
      setError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>Password</FormLabel>
                <Link
                  href="/auth/forgot-password"
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <FormControl>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="pr-10"
                    {...field}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full btn-premium text-white"
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner className="mr-2" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
      </form>
    </Form>
  );
}

// ─── Google OAuth ─────────────────────────────────────────────────────────────

function GoogleTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogle() {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.signIn.social({
        provider: 'google',
        callbackURL: '/auth/callback',
      });
      if (result.error) {
        const errAny = result.error as Record<string, unknown>;
        const message = (errAny.message as string) ?? '';
        if (message.includes('no account') || message.includes('not found')) {
          setError(
            'No account found for this Google account. Please apply for access first.'
          );
        } else if (message.includes('banned')) {
          setError(
            'Your account has been suspended. Contact support for assistance.'
          );
        } else {
          setError('Google sign-in failed. Please try again.');
        }
        setLoading(false);
      }
      // On success, better-auth redirects automatically via callbackURL
    } catch {
      setError('Google sign-in failed. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button
        onClick={handleGoogle}
        variant="outline"
        className="w-full gap-2"
        disabled={loading}
      >
        {loading ? (
          <Spinner className="mr-2" />
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        Sign in with Google
      </Button>
    </div>
  );
}

// ─── Magic Link ───────────────────────────────────────────────────────────────

const magicLinkSchema = z.object({
  email: z
    .string()
    .min(1, 'This field is required')
    .email('Please enter a valid email address'),
});
type MagicLinkValues = z.infer<typeof magicLinkSchema>;

function MagicLinkTab() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<MagicLinkValues>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: { email: '' },
  });

  async function onSubmit(values: MagicLinkValues) {
    setError(null);
    setLoading(true);
    try {
      await authClient.signIn.magicLink({
        email: values.email,
        callbackURL: '/auth/callback',
      });
    } catch {
      // Silently swallow — always show same response
    } finally {
      // Always show success — don't reveal if account exists
      setSent(true);
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div className="py-6 text-center space-y-2">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Mail className="w-5 h-5 text-primary" />
        </div>
        <p className="text-sm font-medium text-foreground">Check your inbox</p>
        <p className="text-xs text-muted-foreground max-w-[260px] mx-auto leading-relaxed">
          If this email is registered, you&apos;ll receive a magic link shortly.
        </p>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          className="w-full btn-premium text-white"
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner className="mr-2" />
              Sending...
            </>
          ) : (
            'Send Magic Link'
          )}
        </Button>
      </form>
    </Form>
  );
}

// ─── Main Login Form ──────────────────────────────────────────────────────────

export function LoginForm() {
  return (
    <div className="w-full max-w-md mx-auto">
      {/* Premium card */}
      <div
        className="relative bg-card rounded-2xl overflow-hidden border border-border/70"
        style={{ boxShadow: 'var(--shadow-xl), var(--glow-purple)' }}
      >
        {/* Gradient top accent */}
        <div className="h-[3px] w-full bg-gradient-to-r from-primary/60 via-primary to-primary/50" />

        <div className="px-7 pt-7 pb-8">
          {/* Header — logo visible only on mobile (desktop shows it in the left panel) */}
          <div className="mb-7">
            <div className="lg:hidden mb-5">
              <Logo imageClassName="h-7 w-auto" />
            </div>
            <h1 className="text-[1.65rem] font-display font-semibold text-foreground tracking-tight leading-tight">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              Sign in to your member account
            </p>
          </div>

          {/* Auth method tabs — underline style, no generic pill */}
          <Tabs defaultValue="email">
            <TabsList className="flex bg-transparent p-0 h-auto gap-0 w-full border-b border-border mb-6 rounded-none">
              <TabsTrigger value="email" className={triggerClass}>
                <Mail className="w-3.5 h-3.5" />
                Email
              </TabsTrigger>
              <TabsTrigger value="google" className={triggerClass}>
                <svg
                  className="w-3.5 h-3.5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Google
              </TabsTrigger>
              <TabsTrigger value="magic" className={triggerClass}>
                <Wand2 className="w-3.5 h-3.5" />
                Magic Link
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="mt-0">
              <EmailPasswordTab />
            </TabsContent>
            <TabsContent value="google" className="mt-0">
              <GoogleTab />
            </TabsContent>
            <TabsContent value="magic" className="mt-0">
              <MagicLinkTab />
            </TabsContent>
          </Tabs>

          {/* Footer */}
          <div className="mt-7 pt-6 border-t border-border/50">
            <p className="text-center text-sm text-muted-foreground">
              New to Purple Venture Studio?{' '}
              <Link
                href="/apply"
                className="text-primary hover:underline font-medium"
              >
                Apply for access
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
